// Data CIVIL da fazenda — a primitiva, compartilhada entre client e servidor.
//
// A Bahia é UTC-3 o ano inteiro (o Brasil acabou com o horário de verão em
// 2019). A Vercel roda em UTC. Entre 21:00 e 23:59 na fazenda, o UTC já está no
// dia seguinte — e é essa janela de três horas que produz, sozinha, uma família
// inteira de bugs neste repo:
//
//   • a operação que vence HOJE aparecendo como atrasada às 21h (corrigido no
//     ciclo 1, com `hojeNaFazenda()` no client);
//   • o quadro do kanban abrindo na semana errada num domingo à noite;
//   • o mês da colheita agrupado errado (PR #163);
//   • e o que este arquivo existe para fechar: `iniciadoEm` lido em UTC. Uma
//     fertirrigação começada às 22:00 é 01:00 UTC do dia seguinte, então no dia
//     seguinte `diaDoInstante(iniciadoEm) < hoje` dava FALSO e a operação não
//     era marcada atrasada — ficava entre as de hoje, sem destaque. Fertirrigação
//     noturna é rotina na Boa Vista; a janela é usada de verdade.
//
// `client/src/lib/data-fazenda.ts` reexporta daqui em vez de manter a própria
// cópia da constante: duas definições do mesmo offset é como as três primeiras
// já aconteceram.

export const OFFSET_FAZENDA_MS = 3 * 60 * 60 * 1000;

/**
 * O dia civil (YYYY-MM-DD) da fazenda em que um INSTANTE caiu.
 *
 * 🔴 Nunca `d.toISOString().slice(0,10)` cru: aquilo é o dia em UTC, não o dia de
 * quem estava no campo. Recebe `Date` ou string ISO (o Drizzle devolve `Date`,
 * mas a mesma linha vinda de JSON chega como string).
 */
export function diaCivilNaFazenda(instante: Date | string): string {
  const d = instante instanceof Date ? instante : new Date(instante);
  return new Date(d.getTime() - OFFSET_FAZENDA_MS).toISOString().slice(0, 10);
}

/**
 * "hh:mm" na hora da FAZENDA (UTC−3) de um instante (ms desde 1970, `Date` ou ISO).
 * Nunca `toLocaleTimeString()` cru: o aparelho pode estar em outro fuso, e "Atualizado
 * às 14:05" tem que ser a hora que o operador vê no relógio da fazenda.
 */
export function horaNaFazenda(instante: number | Date | string): string {
  const ms = typeof instante === "number" ? instante : new Date(instante).getTime();
  const d = new Date(ms - OFFSET_FAZENDA_MS);
  const dois = (n: number) => (n < 10 ? "0" + n : String(n));
  return `${dois(d.getUTCHours())}:${dois(d.getUTCMinutes())}`;
}

/** Soma dias a uma data civil YYYY-MM-DD. Puro, sem fuso: string entra, string sai. */
export function somarDiasCivis(data: string, dias: number): string {
  const d = new Date(data + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/**
 * O primeiro dia do mês SEGUINTE a `mesAno` (YYYY-MM) — o limite ABERTO de um
 * intervalo de mês.
 *
 * 🔴 NÃO EXISTE `${mesAno}-31`. Foi assim que o Início do comprador caiu em
 * produção em 13/09/2026: o Postgres recusa `2026-09-31` com "date/time field
 * value out of range", e a tela inteira virou "Não consegui carregar". Setembro,
 * abril, junho, novembro e todo fevereiro quebrariam do mesmo jeito — quatro
 * meses e meio por ano em que a home do corretor não abre.
 *
 * Nem `-30`, que perde o dia 31 dos meses longos, nem "último dia do mês"
 * calculado à mão. O intervalo é SEMIABERTO: `>= mes-01 AND < mesSeguinte-01`.
 * Ele não tem caso especial — nem fevereiro, nem ano bissexto, nem dezembro — e
 * é o mesmo formato que `date` e `timestamp` aceitam sem conversão.
 */
export function primeiroDiaDoMesSeguinte(mesAno: string): string {
  const ano = Number(mesAno.slice(0, 4));
  const mes = Number(mesAno.slice(5, 7));
  // `mes` já é 1-based; usá-lo como índice 0-based do mês dá o mês seguinte, e o
  // Date resolve o virar-do-ano sozinho (2026-12 → 2027-01).
  return new Date(Date.UTC(ano, mes, 1)).toISOString().slice(0, 10);
}
